/*
  Holding the page still behind an overlay.

  WHY THIS EXISTS RATHER THAN DAWN'S body.overflow-hidden

  Dawn locks the page by putting one class on <body>. Eight components in this
  theme add it and, more to the point, eight remove it — details-modal,
  variant-picker, pickup-availability, cart-disclosure-modal, modal-dialog,
  theme-editor and the cart drawer itself all call

      document.body.classList.remove('overflow-hidden')

  with no idea whether anyone else still needs the page held. Whichever one
  closes last wins. Close a variant picker while the cart drawer is open and
  the page behind the drawer starts scrolling again, and it stays that way
  until something re-locks it — which is why it looked like a first-load bug
  that a refresh "fixed": a refresh simply meant nothing had closed yet.

  So the lock is counted here. Two things holding it means two releases before
  the page moves, and a release from something that never took the lock does
  nothing at all.

  WHY IT IS DRIVEN FROM THE DRAWER'S CLASS, NOT FROM open() AND close()

  A lock applied inside open() is only as reliable as every path that shows
  the drawer. This theme has several — the bag link, add-to-bag re-rendering
  the contents, refreshFromServer, and now the free-gift watcher — and open()
  returns early when the drawer is already active. Watching the .active class
  instead makes the lock a consequence of the drawer being VISIBLE, which is
  the thing we actually care about, and it cannot be missed by a new path
  somebody adds later.

  WHY LENIS HAS TO BE STOPPED TOO

  On a fine pointer the page is scrolled by Lenis, not by the browser: it
  listens for the wheel, cancels it, and drives window.scrollTo itself every
  frame. overflow:hidden makes those calls no-ops, so the page does hold — but
  Lenis goes on accumulating the target the whole time, and on release it
  snaps to wherever the shopper "scrolled" behind the overlay. Stopping it is
  what smooth-scroll-tyohaar.css already says to do:

      "If a modal ever does need the page held still, hold it with
       lenis.stop() alone and leave overflow out of it."

  This is that call site.
*/
(() => {
  // Loaded by the cart drawer and the header both; a second copy would start
  // a fresh, empty count and release locks the first one still holds.
  if (window.tyohScrollLock) return;

  const holders = new Set();

  const apply = () => {
    /*
      A holder that has left the document can never release its own lock — its
      observer goes with it. That happens for real: the theme editor replaces
      the header section, and the drawer rendered above it goes too. Without
      this the page would stay locked with nothing on screen holding it, and
      only a reload would free it.
    */
    holders.forEach((owner) => {
      if (owner instanceof Node && !owner.isConnected) holders.delete(owner);
    });

    const shouldLock = holders.size > 0;
    const isLocked = document.documentElement.classList.contains('tyo-scroll-locked');
    if (shouldLock === isLocked) return;

    document.documentElement.classList.toggle('tyo-scroll-locked', shouldLock);

    /*
      Optional chaining on both: window.lenis is only defined when smooth
      scrolling actually runs, which excludes touch devices, reduced motion,
      the theme editor and the merchant switching it off.
    */
    if (shouldLock) window.lenis?.stop();
    else window.lenis?.start();
  };

  window.tyohScrollLock = {
    lock(owner) {
      holders.add(owner);
      apply();
    },
    release(owner) {
      holders.delete(owner);
      apply();
    },
    get held() {
      return holders.size > 0;
    },
  };

  /* ------------------------------------------------------- the cart drawer */

  const watched = new WeakSet();

  const watch = (drawer) => {
    if (watched.has(drawer)) return;
    watched.add(drawer);

    const sync = () => {
      if (drawer.classList.contains('active')) window.tyohScrollLock.lock(drawer);
      else window.tyohScrollLock.release(drawer);
    };

    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['class'] });

    // The drawer can already be open when this runs — a section reload in the
    // theme editor re-runs scripts without closing anything.
    sync();
  };

  const scan = () => document.querySelectorAll('cart-drawer').forEach(watch);

  /*
    The drawer is rendered above the header in theme.liquid, so it is normally
    in the document before this runs. The readyState check covers the case
    where it is not, and shopify:section:load covers the editor replacing the
    header — which can bring a second drawer with it.
  */
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();

  document.addEventListener('shopify:section:load', scan);
})();
